import { useApiScope } from '../scope';
import { fetcher } from '@commenda/utils';
import { useState, useCallback } from 'react';
import type {
  DSLContext,
  DSLErrorResponse,
  DSLExecuteRequest,
  DSLExecuteResponse,
  ExecutionFilter,
  FieldValue,
} from '@commenda/types/src/commenda-os-dsl';
import { sanitizeDSLContext } from './context';
import { DSLExecuteError } from './errors';

const BASE_URL = 'dsl';

function resolveDSLErrorMessage(
  body: DSLExecuteResponse & DSLErrorResponse
): string {
  const { message } = body;
  return (
    (Array.isArray(message) ? message.join('; ') : message) ||
    body.result?.error ||
    body.error ||
    'Failed to execute DSL operation'
  );
}

/**
 * Execute a create or update operation via Commenda OS DSL
 *
 * @param request - The execute request with entity, type, fields, and context
 * @returns The result of the operation
 */
export async function executeCommendaOS(
  request: DSLExecuteRequest,
  shareToken: string | undefined
): Promise<DSLExecuteResponse> {
  const sanitizedRequest = {
    ...request,
    context: sanitizeDSLContext(request.context),
  };
  const response = await fetcher(`${BASE_URL}/execute`, {
    method: 'POST',
    body: sanitizedRequest,
    shareToken,
  });

  // A non-2xx response carries the Nest exception body instead of a result, except from a
  // gateway (502, 504), which answers with an HTML page.
  if (!response.ok) {
    const errorBody: (DSLExecuteResponse & DSLErrorResponse) | undefined =
      await response.json().catch(() => undefined);
    throw errorBody
      ? new DSLExecuteError(resolveDSLErrorMessage(errorBody), errorBody.code)
      : new DSLExecuteError(`The server returned an error (${response.status}).`);
  }

  return response.json();
}

/**
 * Create a new entity via Commenda OS DSL
 *
 * @param entity - The entity type (e.g., 'corporation')
 * @param fields - Array of field-value pairs to set
 * @param context - The DSL context with companyId
 * @returns The result with the new entity ID
 */
export async function createCommendaOSEntity(
  entity: string,
  fields: FieldValue[],
  context: DSLContext,
  shareToken: string | undefined
): Promise<DSLExecuteResponse> {
  return executeCommendaOS({
    execution: {
      type: 'create',
      entity,
      fields,
    },
    context,
  }, shareToken);
}

/**
 * Update an existing entity via Commenda OS DSL
 *
 * @param entity - The entity type (e.g., 'corporation')
 * @param id - The entity ID to update
 * @param fields - Array of field-value pairs to update
 * @param context - The DSL context with companyId
 * @returns The result of the update
 */
export async function updateCommendaOSEntity(
  entity: string,
  id: number | string,
  fields: FieldValue[],
  context: DSLContext,
  shareToken: string | undefined
): Promise<DSLExecuteResponse> {
  return executeCommendaOS({
    execution: {
      type: 'update',
      entity,
      id,
      fields,
    },
    context,
  }, shareToken);
}

/**
 * Upsert an entity via Commenda OS DSL (for relation entities like governmentIdentifier)
 *
 * @param entity - The entity type (e.g., 'governmentIdentifier')
 * @param filters - Array of filters to identify the item (e.g., [{ key: 'key', value: 'US_ENTITY_TAX_NUMBER' }])
 * @param fields - Array of field-value pairs to set
 * @param context - The DSL context with companyId and corporationId
 * @returns The result of the upsert
 */
export async function upsertCommendaOSEntity(
  entity: string,
  filters: ExecutionFilter[],
  fields: FieldValue[],
  context: DSLContext,
  shareToken: string | undefined
): Promise<DSLExecuteResponse> {
  return executeCommendaOS({
    execution: {
      type: 'upsert',
      entity,
      filters,
      fields,
    },
    context,
  }, shareToken);
}

/**
 * Delete an entity via Commenda OS DSL.
 *
 * @param entity - The entity type (e.g., 'shareholding')
 * @param id - The entity ID to delete (number, or string for uuid-keyed entities)
 * @param context - The DSL context with companyId (and corporationId where the entity requires it)
 * @returns The result of the delete
 */
export async function deleteCommendaOSEntity(
  entity: string,
  id: number | string,
  context: DSLContext,
  shareToken: string | undefined
): Promise<DSLExecuteResponse> {
  return executeCommendaOS({
    execution: {
      type: 'delete',
      entity,
      id,
      fields: [],
    },
    context,
  }, shareToken);
}

/**
 * Hook for executing Commenda OS DSL mutations
 * Provides loading state and error handling
 */
export function useCommendaOSExecute() {
  const { shareToken } = useApiScope();

  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastResult, setLastResult] = useState<DSLExecuteResponse | null>(null);

  const execute = useCallback(async (request: DSLExecuteRequest) => {
    setIsExecuting(true);
    setError(null);

    try {
      const result = await executeCommendaOS(request, shareToken);
      setLastResult(result);
      return result;
    } catch (err) {
      const executeError = err instanceof Error ? err : new Error(String(err));
      setError(executeError);
      throw executeError;
    } finally {
      setIsExecuting(false);
    }
  }, [shareToken]);

  const create = useCallback(
    async (entity: string, fields: FieldValue[], context: DSLContext) => {
      return execute({
        execution: {
          type: 'create',
          entity,
          fields,
        },
        context,
      });
    },
    [execute]
  );

  const update = useCallback(
    async (
      entity: string,
      id: number | string,
      fields: FieldValue[],
      context: DSLContext
    ) => {
      return execute({
        execution: {
          type: 'update',
          entity,
          id,
          fields,
        },
        context,
      });
    },
    [execute]
  );

  const reset = useCallback(() => {
    setError(null);
    setLastResult(null);
  }, []);

  return {
    execute,
    create,
    update,
    isExecuting,
    error,
    lastResult,
    reset,
  };
}
